import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useUser, useGlobalAction, useFindMany } from '@gadgetinc/react';
import { api } from '../api';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure
} from '@chakra-ui/react';

const AdminInvitePage = () => {
  const navigate = useNavigate();
  const user = useUser(api);

  // Early return for loading state - BEFORE any other hooks
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Authentication check - handle both string and object role formats
  const isAdmin = user && (
    user?.roles?.some(role => 
      role === 'admin' || role === 'system-admin' || 
      role?.key === 'admin' || role?.key === 'system-admin'
    ) || 
    user?.email === '74spartan@gmail.com'
  );

  // Early return for unauthorized access - BEFORE any other hooks
  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h2 style={{ color: '#ff4757', marginBottom: '1rem' }}>Access Denied</h2>
          <p style={{ marginBottom: '1.5rem' }}>You need administrator privileges to access this page.</p>
          <Link 
            to="/sign-in" 
            style={{
              display: 'inline-block',
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // State for notifications
  const [notification, setNotification] = useState(null);

  // Debug logging for roles
  React.useEffect(() => {
    if (user) {
      console.log('Current user roles:', user.roles);
      console.log('User email:', user.email);
      console.log('Role types:', user.roles?.map(role => typeof role));
    }
  }, [user]);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (user && !isAdmin) {
      console.log('Access denied - user roles:', user.roles);
      navigate('/sign-in');
    }
  }, [user, isAdmin, navigate]);

  // Fetch assistants for multi-select
  const [{ data: assistants, fetching: fetchingAssistants, error: assistantsError }] = useFindMany(api.assistant);

  // Fetch agent configurations for multi-select
  const [{ data: agentConfigurations, fetching: fetchingAgents, error: agentsError }] = useFindMany(api.agentConfiguration, {
    select: { id: true, name: true, description: true, isActive: true },
    filter: { isActive: { equals: true } }
  });

  // Fetch users to get unique institutions
  const [{ data: users, fetching: fetchingUsers, error: usersError }] = useFindMany(api.user, {
    select: { institution: true },
    first: 250
  });

  // Global action hook
  const [{ data: inviteResult, fetching: sendingInvite, error: inviteError }, sendInvite] = useGlobalAction(api.sendInvite);

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    institution: '',
    institutionRole: 'student',
    accessCodeType: 'specific',
    duration: '30_days',
    maxUses: '',
    customMessage: '',
    fromSender: 'general',
    assistantIds: [],
    cohort: '',
    agentAccessMode: 'none',
    agentNames: []
  });

  // Batch invite state
  const [batchEmails, setBatchEmails] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // CSV upload state
  const [csvData, setCsvData] = useState([]);
  const [csvFile, setCsvFile] = useState(null);
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvConfirmed, setCsvConfirmed] = useState(false);

  // Modal state
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  // Form validation
  const [errors, setErrors] = useState({});

  // Institution autocomplete state
  const [institutionInput, setInstitutionInput] = useState('');
  const [showInstitutionDropdown, setShowInstitutionDropdown] = useState(false);
  const [institutionOptions, setInstitutionOptions] = useState([]);

  // Cohort selection state
  const [selectedCohort, setSelectedCohort] = useState('');
  const [availableCohorts, setAvailableCohorts] = useState([]);
  const [fetchingCohorts, setFetchingCohorts] = useState(false);

  // Modal state for create cohort
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [createCohortData, setCreateCohortData] = useState({
    name: '',
    displayName: '',
    cohortType: 'EMT_Basic',
    academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
    isActive: true
  });

  // Get unique institutions from users
  useEffect(() => {
    if (users) {
      const uniqueInstitutions = [...new Set(
        users
          .map(user => user.institution)
          .filter(institution => institution && institution.trim())
      )].sort();
      setInstitutionOptions(uniqueInstitutions);
    }
  }, [users]);

  // Update institution input when formData changes
  useEffect(() => {
    setInstitutionInput(formData.institution || '');
  }, [formData.institution]);

  // Watch for institution changes to fetch cohorts
  useEffect(() => {
    if (formData.institution) {
      fetchCohortsForInstitution(formData.institution);
    } else {
      setAvailableCohorts([]);
      setSelectedCohort('');
      setFormData(prev => ({ ...prev, cohort: '' }));
    }
  }, [formData.institution]);

  // Show notification
  const showNotification = (type, title, message) => {
    setNotification({ type, title, message });
    setTimeout(() => setNotification(null), 5000);
  };

  // Fetch cohorts for selected institution
  const resolveInstitutionName = (rawInstitution) => {
    const trimmed = String(rawInstitution || '').trim();
    if (!trimmed) return '';
    const canonical = institutionOptions.find(
      (opt) => String(opt || '').trim().toLowerCase() === trimmed.toLowerCase()
    );
    return canonical || trimmed;
  };

  const fetchCohortsForInstitution = async (institution) => {
    const canonicalInstitution = resolveInstitutionName(institution);
    if (!canonicalInstitution) {
      setAvailableCohorts([]);
      setSelectedCohort('');
      return;
    }
    
    setFetchingCohorts(true);
    try {
      // Use the existing useFindMany hook to fetch cohorts
      const cohortsData = await api.cohort.findMany({
        filter: { 
          institution: { equals: canonicalInstitution },
          isActive: { equals: true }
        },
        select: {
          id: true,
          name: true,
          displayName: true,
          cohortType: true,
          academicYear: true
        }
      });
      const normalizedCohorts = Array.isArray(cohortsData)
        ? cohortsData
        : cohortsData?.edges?.map((edge) => edge?.node).filter(Boolean) || [];
      setAvailableCohorts(normalizedCohorts);
    } catch (error) {
      console.error('Error fetching cohorts:', error);
      showNotification('error', 'Error', 'Failed to fetch cohorts for institution');
      setAvailableCohorts([]);
    }
    setFetchingCohorts(false);
  };

  // CSV parsing function
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length === 0) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data = [];

    // Try to find email and name columns
    const emailIndex = headers.findIndex(h => h.includes('email'));
    const firstNameIndex = headers.findIndex(h => h.includes('first') || h.includes('fname'));
    const lastNameIndex = headers.findIndex(h => h.includes('last') || h.includes('lname'));
    const nameIndex = headers.findIndex(h => h.includes('name') && !h.includes('first') && !h.includes('last'));

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim().replace(/^"(.*)"$/, '$1'));
      
      if (emailIndex !== -1 && values[emailIndex]) {
        const email = values[emailIndex];
        let firstName = '';
        let lastName = '';

        if (firstNameIndex !== -1) firstName = values[firstNameIndex] || '';
        if (lastNameIndex !== -1) lastName = values[lastNameIndex] || '';
        
        // If no first/last name columns, try to split a single name column
        if (!firstName && !lastName && nameIndex !== -1) {
          const fullName = values[nameIndex] || '';
          const nameParts = fullName.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }

        data.push({ email, firstName, lastName });
      }
    }

    return data;
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setCsvProcessing(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const parsed = parseCSV(text);
        
        if (parsed.length === 0) {
          showNotification('error', 'Parse Error', 'No valid email addresses found in the file');
        } else {
          setCsvData(parsed);
          setCsvFile(file);
          showNotification('success', 'File Parsed', `Found ${parsed.length} email addresses`);
        }
      } catch (error) {
        showNotification('error', 'Parse Error', 'Error parsing CSV file: ' + error.message);
      }
      setCsvProcessing(false);
    };

    reader.readAsText(file);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (activeTab === 0) {
      // Single invite validation
      if (!formData.email) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
    } else if (activeTab === 1) {
      // Batch invite validation
      if (!batchEmails.trim()) {
        newErrors.batchEmails = 'At least one email is required';
      } else {
        const emails = batchEmails.split('\n').filter(email => email.trim());
        const invalidEmails = emails.filter(email => !/\S+@\S+\.\S+/.test(email.trim()));
        if (invalidEmails.length > 0) {
          newErrors.batchEmails = `Invalid emails: ${invalidEmails.join(', ')}`;
        }
      }
    } else if (activeTab === 2) {
      // CSV invite validation
      if (csvData.length === 0) {
        newErrors.csvData = 'Please upload and confirm a CSV file';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleAssistantChange = (assistantId) => {
    setFormData(prev => ({
      ...prev,
      assistantIds: prev.assistantIds.includes(assistantId)
        ? prev.assistantIds.filter(id => id !== assistantId)
        : [...prev.assistantIds, assistantId]
    }));
  };

  const handleAgentChange = (agentName) => {
    setFormData(prev => ({
      ...prev,
      agentNames: prev.agentNames.includes(agentName)
        ? prev.agentNames.filter(name => name !== agentName)
        : [...prev.agentNames, agentName]
    }));
  };

  const handleSingleInvite = async () => {
    if (!validateForm()) return;

    try {
      await sendInvite({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        institution: formData.institution,
        institutionRole: formData.institutionRole,
        accessCodeType: formData.accessCodeType,
        duration: formData.duration,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
        customMessage: formData.customMessage,
        fromSender: formData.fromSender,
        assistantIds: formData.assistantIds,
        cohortId: formData.cohort,
        agentAccessMode: formData.agentAccessMode,
        agentNames: formData.agentNames
      });

      showNotification('success', 'Invitation Sent', `Invitation sent successfully to ${formData.email}`);

      // Reset form
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        institution: '',
        institutionRole: 'student',
        accessCodeType: 'specific',
        duration: '30_days',
        maxUses: '',
        customMessage: '',
        fromSender: 'general',
        assistantIds: [],
        cohort: '',
        agentAccessMode: 'none',
        agentNames: []
      });
    } catch (error) {
      showNotification('error', 'Error Sending Invitation', error.message || 'An error occurred while sending the invitation');
    }
  };

  const handleBatchInvite = async () => {
    if (!validateForm()) return;

    const emails = batchEmails.split('\n').filter(email => email.trim()).map(email => email.trim());
    
    try {
      for (const email of emails) {
        await sendInvite({
          email: email,
          firstName: '',
          lastName: '',
          institution: formData.institution,
          institutionRole: formData.institutionRole,
          accessCodeType: formData.accessCodeType,
          duration: formData.duration,
          maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
          customMessage: formData.customMessage,
          fromSender: formData.fromSender,
          assistantIds: formData.assistantIds,
          cohortId: formData.cohort,
          agentAccessMode: formData.agentAccessMode,
          agentNames: formData.agentNames
        });
      }

      showNotification('success', 'Batch Invitations Sent', `Successfully sent ${emails.length} invitations`);
      setBatchEmails('');
    } catch (error) {
      showNotification('error', 'Error Sending Invitations', error.message || 'An error occurred while sending the batch invitations');
    }
  };

  const handleCSVInvite = async () => {
    if (!validateForm()) return;

    try {
      for (const row of csvData) {
        await sendInvite({
          email: row.email,
          firstName: row.firstName || '',
          lastName: row.lastName || '',
          institution: formData.institution,
          institutionRole: formData.institutionRole,
          accessCodeType: formData.accessCodeType,
          duration: formData.duration,
          maxUses: formData.maxUses ? parseInt(formData.maxUses) : undefined,
          customMessage: formData.customMessage,
          fromSender: formData.fromSender,
          assistantIds: formData.assistantIds,
          cohortId: formData.cohort,
          agentAccessMode: formData.agentAccessMode,
          agentNames: formData.agentNames
        });
      }

      showNotification('success', 'CSV Invitations Sent', `Successfully sent ${csvData.length} invitations`);
      setCsvData([]);
      setCsvFile(null);
      setCsvConfirmed(false);
    } catch (error) {
      showNotification('error', 'Error Sending CSV Invitations', error.message || 'An error occurred while sending the CSV invitations');
    }
  };

  const getEmailPreview = () => {
    let count = 0;
    if (activeTab === 0 && formData.email) count = 1;
    if (activeTab === 1 && batchEmails.trim()) {
      count = batchEmails.split('\n').filter(email => email.trim()).length;
    }
    if (activeTab === 2) count = csvData.length;
    
    return count;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#111111',
      color: '#ffffff'
    }}>
      {/* Header */}
      <div style={{
        background: '#1a1a1a',
        borderBottom: '1px solid #333',
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link 
            to="/admin" 
            style={{
              background: '#374151',
              color: '#ffffff',
              padding: '8px 16px',
              borderRadius: '6px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #4b5563',
              transition: 'all 0.2s ease'
            }}
          >
            ← Back to Dashboard
          </Link>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            margin: '0',
            color: '#ffffff'
          }}>Send Invitations</h1>
        </div>
        <div style={{
          fontSize: '14px',
          color: '#d1d5db',
          background: '#1e1e1e',
          padding: '8px 12px',
          borderRadius: '6px',
          border: '1px solid #333'
        }}>
          <strong style={{ color: '#ffffff' }}>User:</strong> {user.email}
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          background: notification.type === 'success' ? '#10b981' : '#ef4444',
          color: 'white',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          maxWidth: '400px'
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '16px', fontWeight: '600' }}>
            {notification.title}
          </h4>
          <p style={{ margin: '0', fontSize: '14px' }}>{notification.message}</p>
        </div>
      )}

      <div style={{ padding: '2rem' }}>
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          marginBottom: '2rem',
          borderBottom: '1px solid #333'
        }}>
          {['Single Invite', 'Batch Invite', 'CSV Upload'].map((tab, index) => (
            <button
              key={tab}
              onClick={() => setActiveTab(index)}
              style={{
                background: activeTab === index ? '#3b82f6' : 'transparent',
                color: activeTab === index ? '#ffffff' : '#d1d5db',
                border: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                borderRadius: '8px 8px 0 0',
                transition: 'all 0.2s ease'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem'
        }}>
          {/* Single Invite Tab */}
          {activeTab === 0 && (
            <div>
              <h2 style={{ fontSize: '20px', marginBottom: '1.5rem', color: '#ffffff' }}>
                Send Single Invitation
              </h2>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: errors.email ? '1px solid #ef4444' : '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="user@example.com"
                  />
                  {errors.email && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '0.25rem' }}>
                      {errors.email}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="John"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="Doe"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Institution
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={institutionInput}
                      onChange={(e) => {
                        setInstitutionInput(e.target.value);
                        handleInputChange('institution', e.target.value);
                        setShowInstitutionDropdown(true);
                      }}
                      onFocus={() => setShowInstitutionDropdown(true)}
                      onBlur={() => {
                        setTimeout(() => setShowInstitutionDropdown(false), 200);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                      placeholder="University Hospital, Fire Dept, etc."
                    />
                    {showInstitutionDropdown && institutionOptions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        marginTop: '2px'
                      }}>
                        {institutionOptions
                          .filter(institution => 
                            institution.toLowerCase().includes(institutionInput.toLowerCase())
                          )
                          .map((institution) => (
                            <div
                              key={institution}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setInstitutionInput(institution);
                                handleInputChange('institution', institution);
                                setShowInstitutionDropdown(false);
                              }}
                              style={{
                                padding: '12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #404040',
                                color: '#ffffff',
                                fontSize: '14px',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#374151';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              {institution}
                            </div>
                          ))}
                        {institutionInput && 
                         !institutionOptions.some(inst => 
                           inst.toLowerCase() === institutionInput.toLowerCase()
                         ) && (
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleInputChange('institution', institutionInput.trim());
                              setShowInstitutionDropdown(false);
                            }}
                            style={{
                              padding: '12px',
                              cursor: 'pointer',
                              color: '#10b981',
                              fontSize: '14px',
                              fontStyle: 'italic',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#374151';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                            }}
                          >
                            Create new: "{institutionInput}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Cohort
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                    <select
                      value={formData.cohort || ''}
                      onChange={(e) => handleInputChange('cohort', e.target.value)}
                      disabled={!formData.institution || fetchingCohorts}
                      style={{
                        flex: '1',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select cohort (optional)</option>
                      {availableCohorts.map(cohort => (
                        <option key={cohort.id} value={cohort.id}>
                          {cohort.displayName || cohort.name} ({cohort.cohortType} - {cohort.academicYear})
                        </option>
                      ))}
                    </select>
                    {formData.institution && (
                      <button
                        type="button"
                        onClick={onOpen}
                        style={{
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          padding: '12px 16px',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          minWidth: '120px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Create New
                      </button>
                    )}
                  </div>
                  {fetchingCohorts && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      Loading cohorts...
                    </div>
                  )}
                  {formData.institution && availableCohorts.length === 0 && !fetchingCohorts && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      No active cohorts found for this institution
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Institution Role
                  </label>
                  <select
                    value={formData.institutionRole}
                    onChange={(e) => handleInputChange('institutionRole', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="administrator">Administrator</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Access Code Type
                  </label>
                  <select
                    value={formData.accessCodeType}
                    onChange={(e) => handleInputChange('accessCodeType', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="specific">Specific Assistants</option>
                    <option value="all">All Assistants</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Duration
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="30_days">30 Days</option>
                    <option value="90_days">90 Days</option>
                    <option value="1_year">1 Year</option>
                    <option value="unlimited">Unlimited</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Max Uses (Optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxUses}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (parseInt(value) >= 0 && !isNaN(parseInt(value)))) {
                        handleInputChange('maxUses', value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && (parseInt(value) < 0 || isNaN(parseInt(value)))) {
                        handleInputChange('maxUses', '');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Send From
                  </label>
                  <select
                    value={formData.fromSender}
                    onChange={(e) => handleInputChange('fromSender', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="general">Elevation EMS Team</option>
                    <option value="rick">Rick (Personal)</option>
                  </select>
                </div>
              </div>

              {/* Assistant Selection */}
              {assistants && assistants.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Grant Access to Assistants (Optional)
                  </label>
                  <div style={{
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    padding: '12px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {assistants.map((assistant) => (
                      <label key={assistant.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.assistantIds.includes(assistant.id)}
                          onChange={() => handleAssistantChange(assistant.id)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ color: '#ffffff', fontSize: '14px' }}>
                          {assistant.name}
                        </span>
                        {assistant.description && (
                          <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                            - {assistant.description}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent Access Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  Agent Access
                </label>
                <div style={{
                  background: '#2a2a2a',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  padding: '12px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <input
                        type="radio"
                        name="agentAccessMode"
                        value="none"
                        checked={formData.agentAccessMode === 'none'}
                        onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>
                        No Agent Access
                      </span>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <input
                        type="radio"
                        name="agentAccessMode"
                        value="specific"
                        checked={formData.agentAccessMode === 'specific'}
                        onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>
                        Specific Agents
                      </span>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}>
                      <input
                        type="radio"
                        name="agentAccessMode"
                        value="all"
                        checked={formData.agentAccessMode === 'all'}
                        onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>
                        All Agents
                      </span>
                    </label>
                  </div>
                  
                  {formData.agentAccessMode === 'specific' && agentConfigurations && agentConfigurations.length > 0 && (
                    <div style={{
                      background: '#1a1a1a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      padding: '12px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {agentConfigurations.map((agent) => (
                        <label key={agent.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background 0.2s ease'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.agentNames.includes(agent.name)}
                            onChange={() => handleAgentChange(agent.name)}
                            style={{ marginRight: '8px' }}
                          />
                          <span style={{ color: '#ffffff', fontSize: '14px' }}>
                            {agent.name}
                          </span>
                          {agent.description && (
                            <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                              - {agent.description}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {formData.agentAccessMode === 'specific' && (!agentConfigurations || agentConfigurations.length === 0) && (
                    <div style={{
                      color: '#9ca3af',
                      fontSize: '14px',
                      padding: '8px',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}>
                      No active agent configurations available
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  Custom Message (Optional)
                </label>
                <textarea
                  value={formData.customMessage}
                  onChange={(e) => handleInputChange('customMessage', e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Add a personal message to the invitation email..."
                />
              </div>

              <button
                onClick={handleSingleInvite}
                disabled={sendingInvite}
                style={{
                  background: sendingInvite ? '#6b7280' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: sendingInvite ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {sendingInvite ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          )}

          {/* Batch Invite Tab */}
          {activeTab === 1 && (
            <div>
              <h2 style={{ fontSize: '20px', marginBottom: '1.5rem', color: '#ffffff' }}>
                Send Batch Invitations
              </h2>
              
              {/* Batch Assignment Settings */}
              <div style={{
                background: '#2a2a2a',
                border: '1px solid #404040',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '1rem', color: '#ffffff' }}>
                  Batch Assignment Settings
                </h3>
                
                {/* Info banner */}
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '1rem',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'start'
                }}>
                  <div style={{ color: '#92400e', fontSize: '18px', fontWeight: 'bold' }}>ℹ️</div>
                  <div style={{ color: '#92400e', fontSize: '14px' }}>
                    The institution and cohort selected below will be assigned to <strong>ALL</strong> invitations in this batch.
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Institution field */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db', fontWeight: '500' }}>
                      Institution (applies to all)
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={institutionInput}
                        onChange={(e) => {
                          setInstitutionInput(e.target.value);
                          handleInputChange('institution', e.target.value);
                          setShowInstitutionDropdown(true);
                        }}
                        onFocus={() => setShowInstitutionDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => setShowInstitutionDropdown(false), 200);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                        placeholder="Select or enter institution name"
                      />
                      {showInstitutionDropdown && institutionOptions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          marginTop: '2px'
                        }}>
                          {institutionOptions
                            .filter(institution => 
                              institution.toLowerCase().includes(institutionInput.toLowerCase())
                            )
                            .map((institution) => (
                              <div
                                key={institution}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setInstitutionInput(institution);
                                  handleInputChange('institution', institution);
                                  setShowInstitutionDropdown(false);
                                }}
                                style={{
                                  padding: '12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #404040',
                                  color: '#ffffff',
                                  fontSize: '14px',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#374151';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                }}
                              >
                                {institution}
                              </div>
                            ))}
                          {institutionInput && 
                           !institutionOptions.some(inst => 
                             inst.toLowerCase() === institutionInput.toLowerCase()
                           ) && (
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleInputChange('institution', institutionInput.trim());
                                setShowInstitutionDropdown(false);
                              }}
                              style={{
                                padding: '12px',
                                cursor: 'pointer',
                                color: '#10b981',
                                fontSize: '14px',
                                fontStyle: 'italic',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#374151';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              Create new: "{institutionInput}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cohort field */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db', fontWeight: '500' }}>
                      Cohort (applies to all)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      <select
                        value={formData.cohort || ''}
                        onChange={(e) => handleInputChange('cohort', e.target.value)}
                        disabled={!formData.institution || fetchingCohorts}
                        style={{
                          flex: '1',
                          padding: '12px',
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px',
                          cursor: !formData.institution || fetchingCohorts ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Select cohort (optional)</option>
                        {availableCohorts.map(cohort => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.displayName || cohort.name} ({cohort.cohortType} - {cohort.academicYear})
                          </option>
                        ))}
                      </select>
                      {formData.institution && (
                        <button
                          type="button"
                          onClick={onOpen}
                          style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            minWidth: '120px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Create New
                        </button>
                      )}
                    </div>
                    {fetchingCohorts && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        Loading cohorts...
                      </div>
                    )}
                    {formData.institution && availableCohorts.length === 0 && !fetchingCohorts && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        No active cohorts found for this institution
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  Email Addresses (one per line) *
                </label>
                <textarea
                  value={batchEmails}
                  onChange={(e) => {
                    setBatchEmails(e.target.value);
                    if (errors.batchEmails) {
                      setErrors(prev => ({ ...prev, batchEmails: undefined }));
                    }
                  }}
                  rows={10}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: errors.batchEmails ? '1px solid #ef4444' : '1px solid #404040',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder={`user1@example.com
user2@example.com
user3@example.com`}
                />
                {errors.batchEmails && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '0.25rem' }}>
                    {errors.batchEmails}
                  </div>
                )}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Institution
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={institutionInput}
                      onChange={(e) => {
                        setInstitutionInput(e.target.value);
                        handleInputChange('institution', e.target.value);
                        setShowInstitutionDropdown(true);
                      }}
                      onFocus={() => setShowInstitutionDropdown(true)}
                      onBlur={() => {
                        setTimeout(() => setShowInstitutionDropdown(false), 200);
                      }}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                      placeholder="University Hospital, Fire Dept, etc."
                    />
                    {showInstitutionDropdown && institutionOptions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        marginTop: '2px'
                      }}>
                        {institutionOptions
                          .filter(institution => 
                            institution.toLowerCase().includes(institutionInput.toLowerCase())
                          )
                          .map((institution) => (
                            <div
                              key={institution}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setInstitutionInput(institution);
                                handleInputChange('institution', institution);
                                setShowInstitutionDropdown(false);
                              }}
                              style={{
                                padding: '12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #404040',
                                color: '#ffffff',
                                fontSize: '14px',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#374151';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              {institution}
                            </div>
                          ))}
                        {institutionInput && 
                         !institutionOptions.some(inst => 
                           inst.toLowerCase() === institutionInput.toLowerCase()
                         ) && (
                          <div
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleInputChange('institution', institutionInput.trim());
                              setShowInstitutionDropdown(false);
                            }}
                            style={{
                              padding: '12px',
                              cursor: 'pointer',
                              color: '#10b981',
                              fontSize: '14px',
                              fontStyle: 'italic',
                              transition: 'background 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = '#374151';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'transparent';
                            }}
                          >
                            Create new: "{institutionInput}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Institution Role
                  </label>
                  <select
                    value={formData.institutionRole}
                    onChange={(e) => handleInputChange('institutionRole', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="student">Student</option>
                    <option value="instructor">Instructor</option>
                    <option value="administrator">Administrator</option>
                    <option value="staff">Staff</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Access Code Type
                  </label>
                  <select
                    value={formData.accessCodeType}
                    onChange={(e) => handleInputChange('accessCodeType', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="specific">Specific Assistants</option>
                    <option value="all">All Assistants</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Duration
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => handleInputChange('duration', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="30_days">30 Days</option>
                    <option value="90_days">90 Days</option>
                    <option value="1_year">1 Year</option>
                    <option value="unlimited">Unlimited</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Max Uses (Optional)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxUses}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (parseInt(value) >= 0 && !isNaN(parseInt(value)))) {
                        handleInputChange('maxUses', value);
                      }
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value !== '' && (parseInt(value) < 0 || isNaN(parseInt(value)))) {
                        handleInputChange('maxUses', '');
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="Leave empty for unlimited"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Send From
                  </label>
                  <select
                    value={formData.fromSender}
                    onChange={(e) => handleInputChange('fromSender', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                  >
                    <option value="general">Elevation EMS Team</option>
                    <option value="rick">Rick (Personal)</option>
                  </select>
                </div>
              </div>

              {/* Assistant Selection */}
              {assistants && assistants.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Grant Access to Assistants (Optional)
                  </label>
                  <div style={{
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    padding: '12px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                  }}>
                    {assistants.map((assistant) => (
                      <label key={assistant.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px',
                        cursor: 'pointer',
                        borderRadius: '4px',
                        transition: 'background 0.2s ease'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.assistantIds.includes(assistant.id)}
                          onChange={() => handleAssistantChange(assistant.id)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ color: '#ffffff', fontSize: '14px' }}>
                          {assistant.name}
                        </span>
                        {assistant.description && (
                          <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                            - {assistant.description}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Agent Access Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  Agent Access
                </label>
                <div style={{
                  background: '#2a2a2a',
                  border: '1px solid #404040',
                  borderRadius: '6px',
                  padding: '12px'
                }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <input
                        type="radio"
                        name="agentAccessMode"
                        value="none"
                        checked={formData.agentAccessMode === 'none'}
                        onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>
                        No Agent Access
                      </span>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <input
                        type="radio"
                        name="agentAccessMode"
                        value="specific"
                        checked={formData.agentAccessMode === 'specific'}
                        onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>
                        Specific Agents
                      </span>
                    </label>
                    
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}>
                      <input
                        type="radio"
                        name="agentAccessMode"
                        value="all"
                        checked={formData.agentAccessMode === 'all'}
                        onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ color: '#ffffff', fontSize: '14px' }}>
                        All Agents
                      </span>
                    </label>
                  </div>
                  
                  {formData.agentAccessMode === 'specific' && agentConfigurations && agentConfigurations.length > 0 && (
                    <div style={{
                      background: '#1a1a1a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      padding: '12px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {agentConfigurations.map((agent) => (
                        <label key={agent.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          transition: 'background 0.2s ease'
                        }}>
                          <input
                            type="checkbox"
                            checked={formData.agentNames.includes(agent.name)}
                            onChange={() => handleAgentChange(agent.name)}
                            style={{ marginRight: '8px' }}
                          />
                          <span style={{ color: '#ffffff', fontSize: '14px' }}>
                            {agent.name}
                          </span>
                          {agent.description && (
                            <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                              - {agent.description}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {formData.agentAccessMode === 'specific' && (!agentConfigurations || agentConfigurations.length === 0) && (
                    <div style={{
                      color: '#9ca3af',
                      fontSize: '14px',
                      padding: '8px',
                      textAlign: 'center',
                      fontStyle: 'italic'
                    }}>
                      No active agent configurations available
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  Custom Message (Optional)
                </label>
                <textarea
                  value={formData.customMessage}
                  onChange={(e) => handleInputChange('customMessage', e.target.value)}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                  placeholder="Add a personal message to the invitation email..."
                />
              </div>

              <button
                onClick={handleBatchInvite}
                disabled={sendingInvite}
                style={{
                  background: sendingInvite ? '#6b7280' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: sendingInvite ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {sendingInvite ? 'Sending...' : `Send ${getEmailPreview()} Invitations`}
              </button>
            </div>
          )}

          {/* CSV Upload Tab */}
          {activeTab === 2 && (
            <div>
              <h2 style={{ fontSize: '20px', marginBottom: '1.5rem', color: '#ffffff' }}>
                Upload CSV File
              </h2>
              
              {/* Batch Assignment Settings */}
              <div style={{
                background: '#2a2a2a',
                border: '1px solid #404040',
                borderRadius: '8px',
                padding: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '1rem', color: '#ffffff' }}>
                  Batch Assignment Settings
                </h3>
                
                {/* Info banner */}
                <div style={{
                  background: '#fef3c7',
                  border: '1px solid #fbbf24',
                  borderRadius: '6px',
                  padding: '12px',
                  marginBottom: '1rem',
                  display: 'flex',
                  gap: '8px',
                  alignItems: 'start'
                }}>
                  <div style={{ color: '#92400e', fontSize: '18px', fontWeight: 'bold' }}>ℹ️</div>
                  <div style={{ color: '#92400e', fontSize: '14px' }}>
                    The institution and cohort selected below will be assigned to <strong>ALL</strong> invitations in this batch.
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  {/* Institution field */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db', fontWeight: '500' }}>
                      Institution (applies to all)
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={institutionInput}
                        onChange={(e) => {
                          setInstitutionInput(e.target.value);
                          handleInputChange('institution', e.target.value);
                          setShowInstitutionDropdown(true);
                        }}
                        onFocus={() => setShowInstitutionDropdown(true)}
                        onBlur={() => {
                          setTimeout(() => setShowInstitutionDropdown(false), 200);
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                        placeholder="Select or enter institution name"
                      />
                      {showInstitutionDropdown && institutionOptions.length > 0 && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          marginTop: '2px'
                        }}>
                          {institutionOptions
                            .filter(institution => 
                              institution.toLowerCase().includes(institutionInput.toLowerCase())
                            )
                            .map((institution) => (
                              <div
                                key={institution}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setInstitutionInput(institution);
                                  handleInputChange('institution', institution);
                                  setShowInstitutionDropdown(false);
                                }}
                                style={{
                                  padding: '12px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #404040',
                                  color: '#ffffff',
                                  fontSize: '14px',
                                  transition: 'background 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = '#374151';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = 'transparent';
                                }}
                              >
                                {institution}
                              </div>
                            ))}
                          {institutionInput && 
                           !institutionOptions.some(inst => 
                             inst.toLowerCase() === institutionInput.toLowerCase()
                           ) && (
                            <div
                              onMouseDown={(e) => {
                                e.preventDefault();
                                handleInputChange('institution', institutionInput.trim());
                                setShowInstitutionDropdown(false);
                              }}
                              style={{
                                padding: '12px',
                                cursor: 'pointer',
                                color: '#10b981',
                                fontSize: '14px',
                                fontStyle: 'italic',
                                transition: 'background 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#374151';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                              }}
                            >
                              Create new: "{institutionInput}"
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cohort field */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db', fontWeight: '500' }}>
                      Cohort (applies to all)
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
                      <select
                        value={formData.cohort || ''}
                        onChange={(e) => handleInputChange('cohort', e.target.value)}
                        disabled={!formData.institution || fetchingCohorts}
                        style={{
                          flex: '1',
                          padding: '12px',
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px',
                          cursor: !formData.institution || fetchingCohorts ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="">Select cohort (optional)</option>
                        {availableCohorts.map(cohort => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.displayName || cohort.name} ({cohort.cohortType} - {cohort.academicYear})
                          </option>
                        ))}
                      </select>
                      {formData.institution && (
                        <button
                          type="button"
                          onClick={onOpen}
                          style={{
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            padding: '12px 16px',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            minWidth: '120px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          Create New
                        </button>
                      )}
                    </div>
                    {fetchingCohorts && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        Loading cohorts...
                      </div>
                    )}
                    {formData.institution && availableCohorts.length === 0 && !fetchingCohorts && (
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                        No active cohorts found for this institution
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                  CSV File *
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '0.5rem' }}>
                  CSV should contain columns: email, firstName (optional), lastName (optional)
                </div>
                {errors.csvData && (
                  <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '0.25rem' }}>
                    {errors.csvData}
                  </div>
                )}
              </div>

              {csvProcessing && (
                <div style={{
                  background: '#374151',
                  border: '1px solid #4b5563',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1.5rem',
                  textAlign: 'center',
                  color: '#d1d5db'
                }}>
                  Processing CSV file...
                </div>
              )}

              {csvData.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ fontSize: '16px', marginBottom: '1rem', color: '#ffffff' }}>
                    CSV Preview ({csvData.length} records)
                  </h3>
                  <div style={{
                    background: '#2a2a2a',
                    border: '1px solid #404040',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse'
                    }}>
                      <thead style={{
                        background: '#374151',
                        position: 'sticky',
                        top: 0
                      }}>
                        <tr>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderBottom: '1px solid #404040'
                          }}>Email</th>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderBottom: '1px solid #404040'
                          }}>First Name</th>
                          <th style={{
                            padding: '12px',
                            textAlign: 'left',
                            color: '#ffffff',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderBottom: '1px solid #404040'
                          }}>Last Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.map((row, index) => (
                          <tr key={index} style={{
                            borderBottom: index < csvData.length - 1 ? '1px solid #404040' : 'none'
                          }}>
                            <td style={{
                              padding: '12px',
                              color: '#ffffff',
                              fontSize: '14px'
                            }}>{row.email}</td>
                            <td style={{
                              padding: '12px',
                              color: '#d1d5db',
                              fontSize: '14px'
                            }}>{row.firstName || '-'}</td>
                            <td style={{
                              padding: '12px',
                              color: '#d1d5db',
                              fontSize: '14px'
                            }}>{row.lastName || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {csvData.length > 0 && (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                        Access Code Type
                      </label>
                      <select
                        value={formData.accessCodeType}
                        onChange={(e) => handleInputChange('accessCodeType', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#2a2a2a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                      >
                        <option value="specific">Specific Assistants</option>
                        <option value="all">All Assistants</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                        Duration
                      </label>
                      <select
                        value={formData.duration}
                        onChange={(e) => handleInputChange('duration', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#2a2a2a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                      >
                        <option value="30_days">30 Days</option>
                        <option value="90_days">90 Days</option>
                        <option value="1_year">1 Year</option>
                        <option value="unlimited">Unlimited</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                        Max Uses (Optional)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.maxUses}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (parseInt(value) >= 0 && !isNaN(parseInt(value)))) {
                            handleInputChange('maxUses', value);
                          }
                        }}
                        onBlur={(e) => {
                          const value = e.target.value;
                          if (value !== '' && (parseInt(value) < 0 || isNaN(parseInt(value)))) {
                            handleInputChange('maxUses', '');
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#2a2a2a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                        placeholder="Leave empty for unlimited"
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                        Send From
                      </label>
                      <select
                        value={formData.fromSender}
                        onChange={(e) => handleInputChange('fromSender', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          background: '#2a2a2a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          color: '#ffffff',
                          fontSize: '14px'
                        }}
                      >
                        <option value="general">Elevation EMS Team</option>
                        <option value="rick">Rick (Personal)</option>
                      </select>
                    </div>
                  </div>

                  {/* Assistant Selection */}
                  {assistants && assistants.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                        Grant Access to Assistants (Optional)
                      </label>
                      <div style={{
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        padding: '12px',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {assistants.map((assistant) => (
                          <label key={assistant.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            transition: 'background 0.2s ease'
                          }}>
                            <input
                              type="checkbox"
                              checked={formData.assistantIds.includes(assistant.id)}
                              onChange={() => handleAssistantChange(assistant.id)}
                              style={{ marginRight: '8px' }}
                            />
                            <span style={{ color: '#ffffff', fontSize: '14px' }}>
                              {assistant.name}
                            </span>
                            {assistant.description && (
                              <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                                - {assistant.description}
                              </span>
                            )}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Agent Access Selection */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Agent Access
                    </label>
                    <div style={{
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      padding: '12px'
                    }}>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '8px'
                        }}>
                          <input
                            type="radio"
                            name="agentAccessMode"
                            value="none"
                            checked={formData.agentAccessMode === 'none'}
                            onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                            style={{ marginRight: '8px' }}
                          />
                          <span style={{ color: '#ffffff', fontSize: '14px' }}>
                            No Agent Access
                          </span>
                        </label>
                        
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px',
                          marginBottom: '8px'
                        }}>
                          <input
                            type="radio"
                            name="agentAccessMode"
                            value="specific"
                            checked={formData.agentAccessMode === 'specific'}
                            onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                            style={{ marginRight: '8px' }}
                          />
                          <span style={{ color: '#ffffff', fontSize: '14px' }}>
                            Specific Agents
                          </span>
                        </label>
                        
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          cursor: 'pointer',
                          borderRadius: '4px'
                        }}>
                          <input
                            type="radio"
                            name="agentAccessMode"
                            value="all"
                            checked={formData.agentAccessMode === 'all'}
                            onChange={(e) => handleInputChange('agentAccessMode', e.target.value)}
                            style={{ marginRight: '8px' }}
                          />
                          <span style={{ color: '#ffffff', fontSize: '14px' }}>
                            All Agents
                          </span>
                        </label>
                      </div>
                      
                      {formData.agentAccessMode === 'specific' && agentConfigurations && agentConfigurations.length > 0 && (
                        <div style={{
                          background: '#1a1a1a',
                          border: '1px solid #404040',
                          borderRadius: '6px',
                          padding: '12px',
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }}>
                          {agentConfigurations.map((agent) => (
                            <label key={agent.id} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px',
                              cursor: 'pointer',
                              borderRadius: '4px',
                              transition: 'background 0.2s ease'
                            }}>
                              <input
                                type="checkbox"
                                checked={formData.agentNames.includes(agent.name)}
                                onChange={() => handleAgentChange(agent.name)}
                                style={{ marginRight: '8px' }}
                              />
                              <span style={{ color: '#ffffff', fontSize: '14px' }}>
                                {agent.name}
                              </span>
                              {agent.description && (
                                <span style={{ color: '#9ca3af', fontSize: '12px', marginLeft: '8px' }}>
                                  - {agent.description}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      )}
                      
                      {formData.agentAccessMode === 'specific' && (!agentConfigurations || agentConfigurations.length === 0) && (
                        <div style={{
                          color: '#9ca3af',
                          fontSize: '14px',
                          padding: '8px',
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}>
                          No active agent configurations available
                        </div>
                      )}
                    </div>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Custom Message (Optional)
                    </label>
                    <textarea
                      value={formData.customMessage}
                      onChange={(e) => handleInputChange('customMessage', e.target.value)}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                      placeholder="Add a personal message to the invitation email..."
                    />
                  </div>

                  <button
                    onClick={handleCSVInvite}
                    disabled={sendingInvite}
                    style={{
                      background: sendingInvite ? '#6b7280' : '#3b82f6',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '500',
                      cursor: sendingInvite ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {sendingInvite ? 'Sending...' : `Send ${getEmailPreview()} Invitations`}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Create Cohort Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="lg">
          <ModalOverlay />
          <ModalContent style={{ background: '#1e1e1e', border: '1px solid #333' }}>
            <ModalHeader style={{ color: '#ffffff', borderBottom: '1px solid #333' }}>
              Create New Cohort
            </ModalHeader>
            <ModalCloseButton style={{ color: '#ffffff' }} />
            <ModalBody style={{ padding: '2rem' }}>
              <div style={{ display: 'grid', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Cohort Name *
                  </label>
                  <input
                    type="text"
                    value={createCohortData.name}
                    onChange={(e) => setCreateCohortData(prev => ({ ...prev, name: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., EMT-Basic-2024-Spring"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={createCohortData.displayName}
                    onChange={(e) => setCreateCohortData(prev => ({ ...prev, displayName: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      color: '#ffffff',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., EMT Basic Spring 2024"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Cohort Type
                    </label>
                    <select
                      value={createCohortData.cohortType}
                      onChange={(e) => setCreateCohortData(prev => ({ ...prev, cohortType: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    >
                      <option value="EMT_Basic">EMT Basic</option>
                      <option value="EMT_Advanced">EMT Advanced</option>
                      <option value="Paramedic">Paramedic</option>
                      <option value="Nursing">Nursing</option>
                      <option value="Field_Internship">Field Internship</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Academic Year
                    </label>
                    <input
                      type="text"
                      value={createCohortData.academicYear}
                      onChange={(e) => setCreateCohortData(prev => ({ ...prev, academicYear: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                      placeholder="2024-2025"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={createCohortData.startDate}
                      onChange={(e) => setCreateCohortData(prev => ({ ...prev, startDate: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: '#d1d5db' }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={createCohortData.endDate}
                      onChange={(e) => setCreateCohortData(prev => ({ ...prev, endDate: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px',
                        background: '#2a2a2a',
                        border: '1px solid #404040',
                        borderRadius: '6px',
                        color: '#ffffff',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </div>
            </ModalBody>

            <ModalFooter style={{ borderTop: '1px solid #333', gap: '1rem' }}>
              <button
                onClick={onClose}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const newCohort = await api.cohort.create({
                      ...createCohortData,
                      institution: formData.institution,
                      startDate: new Date(createCohortData.startDate),
                      endDate: new Date(createCohortData.endDate)
                    });
                    
                    showNotification('success', 'Cohort Created', `Successfully created cohort: ${newCohort.displayName || newCohort.name}`);
                    
                    // Refresh cohorts list
                    await fetchCohortsForInstitution(formData.institution);
                    
                    // Select the new cohort
                    setFormData(prev => ({ ...prev, cohort: newCohort.id }));
                    
                    // Reset form and close modal
                    setCreateCohortData({
                      name: '',
                      displayName: '',
                      cohortType: 'EMT_Basic',
                      academicYear: new Date().getFullYear() + '-' + (new Date().getFullYear() + 1),
                      startDate: new Date().toISOString().split('T')[0],
                      endDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
                      isActive: true
                    });
                    onClose();
                  } catch (error) {
                    showNotification('error', 'Error Creating Cohort', error.message || 'Failed to create cohort');
                  }
                }}
                disabled={!createCohortData.name || !createCohortData.displayName}
                style={{
                  background: (!createCohortData.name || !createCohortData.displayName) ? '#6b7280' : '#3b82f6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: (!createCohortData.name || !createCohortData.displayName) ? 'not-allowed' : 'pointer'
                }}
              >
                Create Cohort
              </button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};

export default AdminInvitePage;
                
